import React, { useEffect, useState } from 'react';
import { X, Star, MessageSquare, MonitorPlay, Languages, BookmarkPlus, Loader2 } from 'lucide-react';
import ModalHeader from './modal/ModalHeader';
import ModalBody from './modal/ModalBody';
import MobileStepTabs from './modal/MobileStepTabs';
import ReviewButton from './modal/ReviewButton';
import DraftInterviewModal from './DraftInterviewModal';
import useInterviewFlow, { makeId } from '../hooks/useInterviewFlow';
import { useInterviewDuration, useLengthLabel } from '../hooks/useInterviewDuration';
import { useInterviewNaming, useInterviewReview } from '../hooks/useInterviewReview';
import { templatesApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

export { makeId };

const MODULE_CATALOG = [
  { id: 'skill-conversations', label: 'Skill conversations', Icon: Star },
  { id: 'custom-questions', label: 'Custom questions', Icon: MessageSquare },
  { id: 'exercise', label: 'Exercise', Icon: MonitorPlay },
  { id: 'resume-conversation', label: 'Resume conversation', Icon: MonitorPlay },
  { id: 'language-assessment', label: 'Language assessment', Icon: Languages },
];

const LONG_INTERVIEW_THRESHOLD = 75;

function ModalShell({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-[#1A1533]/45 backdrop-blur-[1px]" onClick={onClose} />
      <div className="relative bg-white rounded-[20px] shadow-2xl w-[96vw] max-w-[1280px] h-[94vh] lg:h-[90vh] max-h-[860px] overflow-hidden flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 md:w-9 md:h-9 rounded-full bg-white hover:bg-[#F1ECFE] flex items-center justify-center text-[#4A4463] transition-colors shadow-sm"
        >
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

function ProgressBar({ progress }) {
  return (
    <div className="h-1.5 bg-[#EEE9FB] relative">
      <div
        className="absolute inset-y-0 left-0 progress-bar-fill transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

function SaveAsTemplateButton({ onClick, saving }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      data-testid="save-as-template-btn"
      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E5DEF9] text-[#5B4FE8] text-[12.5px] font-semibold hover:bg-[#F5F1FE] disabled:opacity-50"
    >
      {saving ? <Loader2 size={13} className="animate-spin" /> : <BookmarkPlus size={13} />}
      {saving ? 'Saving…' : 'Save as template'}
    </button>
  );
}

export default function CreateInterviewModal({ open, onOpenChange, onSaved, onTemplateSaved, preset }) {
  const close = () => onOpenChange(false);
  const { toast } = useToast();
  const { name, setName, openingPhrase, setOpeningPhrase } = useInterviewNaming();
  const { flow, setFlow, selected, selectedId, setSelectedId,
    addModule, removeModule, updateModule } = useInterviewFlow();
  const [mobileTab, setMobileTab] = useState('modules');
  const [draftOpen, setDraftOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const totalDuration = useInterviewDuration(flow);
  const lengthLabel = useLengthLabel(totalDuration);
  const progress = Math.min(100, (totalDuration / 45) * 100);
  const tooLong = totalDuration > LONG_INTERVIEW_THRESHOLD;

  // Apply preset (from template click) when modal opens
  useEffect(() => {
    if (open && preset) {
      if (preset.name) setName(preset.name);
      if (preset.opening_phrase) setOpeningPhrase(preset.opening_phrase);
      if (Array.isArray(preset.flow) && preset.flow.length > 0) {
        setFlow(preset.flow);
        setSelectedId(preset.flow[0]?.id || 'introduction');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preset]);

  const { submit, submitting } = useInterviewReview({ onClose: close, onSaved });

  const handleReview = () => {
    if (tooLong) {
      const confirmed = window.confirm(
        `This interview is ${totalDuration} minutes long, which is longer than the recommended 75 minutes. Long interviews can lead to candidate fatigue and lower completion rates. Save anyway?`
      );
      if (!confirmed) return;
    }
    submit({ name, flow, openingPhrase, totalDuration });
  };

  const handleAdd = (type) => addModule(type, { onAfterAdd: () => setMobileTab('details') });
  const handleSelect = (id) => { setSelectedId(id); setMobileTab('details'); };

  const handleApplyDraft = (draft) => {
    if (draft?.name) setName(draft.name);
    if (draft?.opening_phrase) setOpeningPhrase(draft.opening_phrase);
    if (Array.isArray(draft?.flow) && draft.flow.length > 0) {
      setFlow(draft.flow);
      setSelectedId(draft.flow[0]?.id || 'introduction');
      setMobileTab('details');
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!name.trim()) {
      toast({ title: 'Name required', description: 'Name the interview before saving as template.' });
      return;
    }
    setSavingTemplate(true);
    try {
      await templatesApi.create({
        name: name.trim(),
        description: `${flow.length} modules · ${totalDuration} min`,
        flow,
        opening_phrase: openingPhrase,
        total_duration: totalDuration,
      });
      toast({ title: 'Template saved', description: `"${name}" is now in your templates.` });
      onTemplateSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save template.';
      toast({ title: 'Save failed', description: msg });
    } finally {
      setSavingTemplate(false);
    }
  };

  if (!open) return null;

  return (
    <ModalShell onClose={close}>
      <ProgressBar progress={progress} />
      <ModalHeader
        name={name}
        setName={setName}
        lengthLabel={lengthLabel}
        totalDuration={totalDuration}
        onDraftAi={() => setDraftOpen(true)}
      />
      <MobileStepTabs mobileTab={mobileTab} setMobileTab={setMobileTab} flowCount={flow.length} />
      <ModalBody
        catalog={MODULE_CATALOG}
        flow={flow}
        setFlow={setFlow}
        selected={selected}
        selectedId={selectedId}
        mobileTab={mobileTab}
        onAddModule={handleAdd}
        onSelectModule={handleSelect}
        onRemoveModule={removeModule}
        onUpdateModule={(patch) => updateModule(selected.id, patch)}
        openingPhrase={openingPhrase}
        setOpeningPhrase={setOpeningPhrase}
      />
      <ReviewButton
        disabled={!name.trim() || submitting}
        onClick={handleReview}
        submitting={submitting}
        warning={tooLong ? `${totalDuration} min — longer than recommended 75 min.` : null}
        leftSlot={<SaveAsTemplateButton onClick={handleSaveAsTemplate} saving={savingTemplate} />}
      />
      <DraftInterviewModal
        open={draftOpen}
        onClose={() => setDraftOpen(false)}
        onApply={handleApplyDraft}
      />
    </ModalShell>
  );
}
